import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// @mui
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'

// MD
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Layout
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Loader from 'examples/Loader'

// Shared table component (same one Users uses)
import CustomDataTable from '../Users/CustomDataTable'

// Services
import { useAppServices } from 'hook/services'

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'paused', label: 'Paused' },
]

const STATUS_COLORS = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  paused: 'default',
}

const TIER_COLORS = {
  featured: 'warning',
  listed: 'info',
}

function CreativeDirectory() {
  const AppService = useAppServices()
  const navigate = useNavigate()

  const [list, setList] = useState([])
  const [loader, setLoader] = useState(true)
  const [status, setStatus] = useState('all')
  const [search, setSearch] = useState('')
  const typingTimeoutRef = useRef(null)

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalUsers: 0, // CustomPagination expects this key — used as the "total entries" label
  })
  const [counts, setCounts] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    paused: 0,
    total: 0,
  })

  const fetchListings = async (
    page = pagination.page,
    limit = pagination.limit,
    s = status,
    q = search
  ) => {
    setLoader(true)
    try {
      const params = new URLSearchParams({
        status: s,
        page: String(page),
        limit: String(limit),
      })
      if (q?.trim()) params.set('search', q.trim())
      const { response } = await AppService.creativeDirectory.listAdmin({
        query: params.toString(),
      })

      if (response?.success) {
        setList(Array.isArray(response.data) ? response.data : [])
        const meta = response.meta || {}
        setPagination({
          page: meta.page || page,
          limit: meta.limit || limit,
          totalPages: meta.pages || 0,
          totalUsers: meta.total || 0,
        })
        if (meta.counts) setCounts(meta.counts)
      } else {
        setList([])
      }
    } catch (err) {
      console.error('❌ Error fetching directory listings:', err)
      setList([])
    } finally {
      setLoader(false)
    }
  }

  // Initial + tab/search reactions. We reset to page 1 when the filter
  // changes; otherwise an old page index could land out of range.
  useEffect(() => {
    fetchListings(1, pagination.limit, status, search)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  useEffect(() => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => {
      fetchListings(1, pagination.limit, status, search)
    }, 500)
    return () => clearTimeout(typingTimeoutRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const handlePageChange = (newPage) => {
    fetchListings(newPage, pagination.limit, status, search)
  }
  const handleLimitChange = (newLimit) => {
    fetchListings(1, newLimit, status, search)
  }

  const truncate = (text, n = 80) => {
    if (!text) return ''
    return text.length > n ? `${text.slice(0, n)}…` : text
  }

  const dataTableData = {
    columns: [
      { Header: 'Name', accessor: 'name' },
      { Header: 'Profession', accessor: 'profession' },
      { Header: 'City', accessor: 'city' },
      { Header: 'Tier', accessor: 'tier' },
      { Header: 'Status', accessor: 'status' },
      { Header: 'Email', accessor: 'email' },
      { Header: 'Submitted', accessor: 'submitted' },
      { Header: 'Action', accessor: 'action' },
    ],
    rows: list.map((l) => ({
      name: (
        <MDBox display="flex" flexDirection="column">
          <MDTypography variant="button" fontWeight="medium">
            {l.firstName} {l.lastName}
          </MDTypography>
          {l.studioName ? (
            <MDTypography variant="caption" color="text">
              {truncate(l.studioName, 40)}
            </MDTypography>
          ) : null}
        </MDBox>
      ),
      profession:
        Array.isArray(l.professions) && l.professions.length
          ? l.professions.join(', ')
          : l.profession || '—',
      city:
        Array.isArray(l.cities) && l.cities.length
          ? l.cities.join(', ')
          : l.city || '—',
      tier: (
        <Chip
          label={l.tier}
          size="small"
          color={TIER_COLORS[l.tier] || 'default'}
          sx={{ textTransform: 'uppercase', fontSize: 10 }}
        />
      ),
      status: (
        <Chip
          label={l.status}
          size="small"
          color={STATUS_COLORS[l.status] || 'default'}
          sx={{ textTransform: 'uppercase', fontSize: 10 }}
        />
      ),
      email: l.user?.email || '—',
      submitted: l.createdAt ? new Date(l.createdAt).toLocaleDateString() : '—',
      action: (
        <button
          onClick={() => navigate(`/agency/creative-directory/${l._id}`)}
          style={{
            background: '#2563EB',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '6px',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          View
        </button>
      ),
    })),
  }

  return (
    <DashboardLayout>
      {loader && list.length === 0 ? (
        <Loader />
      ) : (
        <>
          <DashboardNavbar />
          <MDBox pt={6} pb={3}>
            <Grid container spacing={6}>
              <Grid item xs={12}>
                <Card>
                  <MDBox
                    mx={2}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <MDTypography variant="h6" color="white">
                      Creative Directory (Total: {counts.total})
                    </MDTypography>
                  </MDBox>

                  <MDBox px={3} pt={3}>
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={12} md sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {STATUS_TABS.map((t) => {
                          const active = t.key === status
                          const badge = t.key === 'all' ? counts.total : counts[t.key]
                          return (
                            <Chip
                              key={t.key}
                              label={`${t.label}${typeof badge === 'number' ? ` (${badge})` : ''}`}
                              clickable
                              onClick={() => setStatus(t.key)}
                              color={active ? 'info' : 'default'}
                              variant={active ? 'filled' : 'outlined'}
                              sx={{ fontWeight: active ? 600 : 400 }}
                            />
                          )
                        })}
                      </Grid>
                      <Grid item xs={12} md="auto">
                        <TextField
                          variant="outlined"
                          size="small"
                          placeholder="Search by name, profession, city..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          sx={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            width: { xs: '100%', md: 280 },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>

                  <MDBox pt={2} px={3}>
                    {list.length === 0 ? (
                      <MDBox py={6} textAlign="center">
                        <MDTypography variant="button" color="text">
                          No {status === 'all' ? '' : status} listings found.
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <CustomDataTable
                        table={dataTableData}
                        canSearch={false}
                        entriesPerPage={{
                          defaultValue: pagination.limit,
                          entries: [5, 10, 15, 20, 25],
                        }}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                      />
                    )}
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        </>
      )}
    </DashboardLayout>
  )
}

export default CreativeDirectory
